<template>
  <button
    ref="reference"
    type="button"
    :aria-expanded="open"
    :aria-label="triggerLabel"
    class="inline-block bg-transparent p-0 text-left"
    :class="[cursorClass, triggerClass]"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @focusin="handleMouseEnter"
    @focusout="handleMouseLeave"
    @click="handleClick"
    @keydown="handleTriggerKeydown"
  >
    <slot />
  </button>

  <teleport to="body">
    <transition name="fade">
      <div
        v-if="open"
        ref="floating"
        role="dialog"
        :aria-label="panelLabel"
        :style="floatingStyles"
        :class="[FLOATING_PANEL_CLS, 'absolute z-50 max-w-[90vw] select-text p-3', panelClass]"
        @mouseenter="handleFloatingMouseEnter"
        @mouseleave="handleFloatingMouseLeave"
        @focusin="handleFloatingMouseEnter"
        @focusout="handleFloatingMouseLeave"
        @keydown="handlePanelKeydown"
      >
        <slot name="content" />
      </div>
    </transition>
  </teleport>
</template>

<script setup lang="ts">
// The interaction half of the admin's popovers: a button trigger, a teleported
// panel, hover with a delay in and a grace period out, a click toggle so a tap
// works, Escape and click-outside. Unlike `Tooltip.vue` the panel is
// `select-text` and takes pointer events. It carries no content of its own.
import { ref, watch, watchEffect, onUnmounted } from "vue";
import { useFloating, offset, shift, flip, autoUpdate } from "@floating-ui/vue";

import { FLOATING_PANEL_CLS } from "./floatingPanelClasses";

const props = withDefaults(
  defineProps<{
    // aria-label replaces the button's text as its accessible name, so
    // whatever the trigger shows has to be in here too.
    triggerLabel: string;
    panelLabel: string;
    side?: "top" | "bottom" | "left" | "right";
    delay?: number;
    panelClass?: string;
    triggerClass?: string;
    // A trigger that reveals a value rather than acting passes
    // "cursor-default". A prop rather than a triggerClass override, since two
    // cursor utilities on one element resolve by stylesheet order.
    cursorClass?: string;
  }>(),
  { side: "bottom", delay: 200, panelClass: "", triggerClass: "", cursorClass: "cursor-pointer" },
);

// Fired each time the panel opens, for a caller whose content has to be fetched. Callers handed
// their content as props ignore it.
const emit = defineEmits<{ (e: "open"): void }>();

// Synchronous, so a close and a reopen inside one tick are two events: a batched watcher would see
// only the final value and report neither.
const open = ref(false);
watch(
  open,
  (isOpen) => {
    if (isOpen) emit("open");
  },
  { flush: "sync" },
);
const reference = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);
const hoverTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
const closeTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

/** Grace period on leaving, so the pointer can travel between the two. */
const CLOSE_DELAY_MS = 150;

const { floatingStyles, update } = useFloating(reference, floating, {
  placement: props.side,
  // Padded, so a panel near the window edge keeps a margin.
  middleware: [offset(8), shift({ padding: 8 }), flip()],
});

onUnmounted(() => {
  if (hoverTimeout.value) clearTimeout(hoverTimeout.value);
  if (closeTimeout.value) clearTimeout(closeTimeout.value);
});

const cancelPendingClose = () => {
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value);
    closeTimeout.value = null;
  }
};

const cancelPendingOpen = () => {
  if (hoverTimeout.value) {
    clearTimeout(hoverTimeout.value);
    hoverTimeout.value = null;
  }
};

const scheduleClose = () => {
  cancelPendingClose();
  closeTimeout.value = setTimeout(() => {
    open.value = false;
  }, CLOSE_DELAY_MS);
};

const handleMouseEnter = () => {
  cancelPendingClose();
  // Bound to both mouseenter and focusin, so tabbing onto an already-hovered
  // trigger arrives here twice. Overwriting the handle orphaned the first
  // timer, and handleMouseLeave then cleared only the second: the panel opened
  // anyway, a delay after the pointer had gone.
  cancelPendingOpen();
  if (props.delay > 0) {
    hoverTimeout.value = setTimeout(() => {
      open.value = true;
    }, props.delay);
  } else {
    open.value = true;
  }
};

const handleMouseLeave = () => {
  cancelPendingOpen();
  scheduleClose();
};

const handleFloatingMouseEnter = () => {
  cancelPendingClose();
};

const handleFloatingMouseLeave = () => {
  scheduleClose();
};

const handleClick = () => {
  // A tap fires mouseenter then click, so the hover timer is armed and would
  // reopen the panel 200ms after a tap meant to close it.
  cancelPendingOpen();
  cancelPendingClose();
  open.value = !open.value;
};

// The panel is teleported to the end of the body, so it is nowhere near the trigger in
// the tab order and the trigger's focusout closes it. Without this, links are mouse-only.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const panelFocusables = (): HTMLElement[] =>
  floating.value ? Array.from(floating.value.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) : [];

// Focusing the trigger fires its own focusin, which arms the open timer, so the
// pending open is cancelled after rather than before.
const closeAndReturnFocus = () => {
  open.value = false;
  reference.value?.focus();
  cancelPendingOpen();
};

const handleTriggerKeydown = (e: KeyboardEvent) => {
  // A panel of plain text has nothing to step into, so Tab behaves as it always
  // did and the focusout closes it.
  if (e.key !== "Tab" || e.shiftKey || !open.value) return;
  const items = panelFocusables();
  if (items.length === 0) return;
  e.preventDefault();
  items[0].focus();
};

const handlePanelKeydown = (e: KeyboardEvent) => {
  if (e.key !== "Tab") return;
  const items = panelFocusables();
  if (items.length === 0) return;
  const edge = e.shiftKey ? items[0] : items[items.length - 1];
  if (document.activeElement !== edge) return;
  e.preventDefault();
  closeAndReturnFocus();
};

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key !== "Escape") return;
  // Closing the panel removes whatever holds focus, so the trigger takes it
  // back rather than leaving the document with nothing focused.
  if (floating.value?.contains(document.activeElement)) {
    closeAndReturnFocus();
    return;
  }
  open.value = false;
};

watchEffect((onCleanup) => {
  if (open.value) {
    window.addEventListener("keydown", handleKeydown);
    onCleanup(() => window.removeEventListener("keydown", handleKeydown));
  }
});

const handleClickOutside = (e: MouseEvent) => {
  const target = e.target as Node;
  if (reference.value && !reference.value.contains(target) && floating.value && !floating.value.contains(target)) {
    open.value = false;
  }
};

watchEffect((onCleanup) => {
  if (open.value) {
    document.addEventListener("mousedown", handleClickOutside);
    onCleanup(() => document.removeEventListener("mousedown", handleClickOutside));
  }
});

watchEffect((onCleanup) => {
  if (reference.value && floating.value && open.value) {
    const cleanup = autoUpdate(reference.value, floating.value, update);
    onCleanup(cleanup);
  }
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
